import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { Typography, Card, CardContent, TextField, Grid, InputAdornment } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import IconButton from "@mui/material/IconButton";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMaterialUIController, useGlobalMDAlert } from "context";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  FP,
  PageHeader,
  StatCard,
  StatusPill,
  fpInputSx,
} from "components/financepro";
import { useApi, usePerfilSucursal } from "lib/api";

const parseNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).split(",").join(""));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) =>
  `$${parseNumber(v).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Buckets de antigüedad por días de atraso de la cuota vencida más vieja
// (diasatraso del backend). Antes salían de la cantidad de cuotas vencidas
// (1 = 0-30 ... 4+ = 90+), que solo cuadraba en préstamos mensuales.
const BUCKETS = [
  { key: "b0_30",   label: "0-30 días",   min: 0,  max: 30,       variant: "success", accent: FP.color.success },
  { key: "b31_60",  label: "31-60 días",  min: 31, max: 60,       variant: "warning", accent: FP.color.warning },
  { key: "b61_90",  label: "61-90 días",  min: 61, max: 90,       variant: "warning", accent: "#FB923C" },
  { key: "b90",     label: "90+ días",    min: 91, max: Infinity, variant: "danger",  accent: FP.color.danger },
];

// Construye el link de WhatsApp a partir del celular. Limpia caracteres no
// numéricos y antepone el código de país (1 = RD) cuando faltan dígitos.
// Incluye un mensaje predeterminado con los datos de la deuda del cliente.
function buildWhatsappLink(row, negocio) {
  let digits = String(row?.celular || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 10) digits = "1" + digits;

  const nombre = (row?.cliente || "").trim();
  const saludo = nombre ? `Estimado/a ${nombre}` : "Estimado/a cliente";
  const mensaje =
    `${saludo}, le saludamos de ${negocio || "nuestra empresa"}.\n\n` +
    `Le recordamos que su préstamo #${row?.id} presenta ${row?.cuotasvencidas} cuota(s) vencida(s), ` +
    `con un monto vencido de ${fmtMoney(row?.total_overdue)}.\n\n` +
    `Le agradecemos comunicarse con nosotros para regularizar su situación. ¡Gracias!`;

  return `https://wa.me/${digits}?text=${encodeURIComponent(mensaje)}`;
}

function getBucketForDias(diasRaw) {
  const d = parseNumber(diasRaw);
  return BUCKETS.find((b) => d >= b.min && d <= b.max) || BUCKETS[0];
}

// El endpoint /api/reportes_balanceantiguedaddesaldo devuelve { data: { noprest: {...} } }.
// Normaliza cada valor a un shape uniforme.
function normalizeRow(raw, key) {
  const id = raw.noprest ?? key;
  const cedula = raw.cedula ?? "";
  const cliente = raw.nombres ?? "";
  const capVenc = parseNumber(raw.capvencido);
  const intVenc = parseNumber(raw.intvencido);
  const cuotas = parseNumber(raw.cuotasvencidas);
  const estado_producto = raw.estado_producto ?? "";
  return {
    id,
    cedula,
    cliente,
    celular: raw.celular ?? raw.telefono ?? "",
    chasis: raw.chasis ?? "",
    marca: raw.marca ?? "",
    modelo: raw.modelo ?? "",
    color: raw.color ?? "",
    periodos: raw.periodos ?? "",
    estado_producto: estado_producto,
    cuotasvencidas: cuotas,
    diasatraso: parseNumber(raw.diasatraso),
    capital_overdue: capVenc,
    interest_overdue: intVenc,
    total_overdue: capVenc + intVenc,
  };
}

// Tipos de financiamiento (prestamo.periodos). Se comparan sin acentos ni mayúsculas.
const TIPOS_FINANCIAMIENTO = [
  { value: "todos", label: "Todos" },
  { value: "meses", label: "Meses" },
  { value: "dias", label: "Días" },
  { value: "quincenas", label: "Quincenas" },
  { value: "semanas", label: "Semanas" },
];

function tipoFinanciamiento(periodos) {
  const t = String(periodos || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (t.startsWith("mes") || t === "mensual") return "meses";
  if (t.startsWith("dia") || t === "diario") return "dias";
  if (t.startsWith("quincen")) return "quincenas";
  if (t.startsWith("seman")) return "semanas";
  return t;
}

function ReporteAntiguedadSaldo() {
  const [fecha1, setFecha1] = useState("");
  const [criterio1, setCriterio1] = useState("");
  const [criterio2, setCriterio2] = useState("");
  const [prestamosPersonales, setPrestamosPersonales] = useState(false);
  const [tipoFin, setTipoFin] = useState("todos");

  const [nosucursal, setNosucursal] = useState(null);
  const [sucursal, setSucursal] = useState([]);

  const [dataObj, setDataObj] = useState({});
  const [busqueda, setBusqueda] = useState("");
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const toggleBucket = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => setCollapsed(Object.fromEntries(BUCKETS.map((b) => [b.key, true])));

  const [controller] = useMaterialUIController();
  const { nombreNegocio, roleWeb } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { esadmin, sucursalPropia } = usePerfilSucursal();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    post("/api/recuperasucursales").then(({ status, data }) => {
      if (status === 200) {
        const list = data["sucursales"] || [];
        // Un usuario que no es admin queda fijado a su sucursal (sin "TODOS").
        if (!esadmin && sucursalPropia) {
          const propia = list.filter(
            (s) => String(s.label || "").split("-")[0].trim() === sucursalPropia
          );
          setSucursal(propia.length ? propia : list);
          setNosucursal(propia[0] || null);
        } else {
          setSucursal([{ label: "0-TODOS" }, ...list]);
        }
      }
      if (status === 400) {
        setSucursal([]);
        showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    return Object.keys(dataObj || {}).map((key) => normalizeRow(dataObj[key], key));
  }, [dataObj]);

  const filteredRows = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (tipoFin === "todos" || tipoFinanciamiento(r.periodos) === tipoFin) &&
        (!q ||
          [r.cliente, r.id, r.cedula, r.chasis].some((v) => String(v || "").toLowerCase().includes(q)))
    );
  }, [rows, busqueda, tipoFin]);

  // Agrupa las filas por bucket en el orden definido.
  const grupos = useMemo(() => {
    const map = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
    filteredRows.forEach((r) => {
      const b = getBucketForDias(r.diasatraso);
      if (b) map[b.key].push(r);
    });
    BUCKETS.forEach((b) => {
      map[b.key].sort((a, z) => z.diasatraso - a.diasatraso || z.cuotasvencidas - a.cuotasvencidas);
    });
    return BUCKETS.map((b) => ({
      ...b,
      items: map[b.key],
      count: map[b.key].length,
      totalAtraso: map[b.key].reduce((acc, r) => acc + r.total_overdue, 0),
    }));
  }, [filteredRows]);

  function procesarrequerimiento() {
    if (!fecha1) {
      showAlert({ message: "Seleccione la fecha de corte", color: "error", autoHideDuration: 3000 });
      return;
    }
    const sucLabel = nosucursal?.label || "";
    const sucParam = !sucLabel || sucLabel.toUpperCase().includes("TODO")
      ? "Todos"
      : sucLabel.split("-")[0];

    setCargando(true);
    post("/api/reportes_balanceantiguedaddesaldo", {
      tipodeusuario: roleWeb || "",
      fecha1: fecha1,
      criterio1: criterio1.length === 0 ? "0" : criterio1,
      criterio2: criterio2.length === 0 ? "" : criterio2,
      sucursal: sucParam,
      prestamospersonales: prestamosPersonales,
    })
      .then(({ status, data }) => {
        if (status === 200) {
          const obj = data["data"] || {};
          setDataObj(obj);
          if (Object.keys(obj).length === 0) {
            showAlert({ message: "No hay préstamos vencidos", color: "info", autoHideDuration: 3000 });
          }
        } else {
          setDataObj({});
          showAlert({ message: data["Error"] || "Error al generar reporte", color: "error", autoHideDuration: 3000 });
        }
      })
      .catch(() => {
        setDataObj({});
        showAlert({ message: "Error de red al generar reporte", color: "error", autoHideDuration: 3000 });
      })
      .finally(() => setCargando(false));
  }

  const totalPrestamos = filteredRows.length;
  const totalCapitalVenc = filteredRows.reduce((acc, r) => acc + r.capital_overdue, 0);
  const totalInteresVenc = filteredRows.reduce((acc, r) => acc + r.interest_overdue, 0);
  const totalAtraso = totalCapitalVenc + totalInteresVenc;
  const promedioCuotas = filteredRows.length
    ? Math.round(filteredRows.reduce((acc, r) => acc + r.cuotasvencidas, 0) / filteredRows.length)
    : 0;

  function handlepdf() {
    if (filteredRows.length === 0) {
      showAlert({ message: "No hay datos para procesar este reporte", color: "error", autoHideDuration: 3000 });
    } else {
      setPresentarpdf(true);
    }
  }

  const MyDocument = () => (
    <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%", colorScheme: "light", background: "#fff" }}>
      <Document>
        <Page size="A4" orientation="landscape" style={{ backgroundColor: "#FFFFFF" }}>
          <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: 15 }} fixed>
            <Text>{nombreNegocio}</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
            <Text>Reporte de Antigüedad de Saldo</Text>
          </View>
          <View style={{ textAlign: "center", width: "94%", fontSize: 9 }} fixed>
            <Text>Fecha de corte: {fecha1 || "—"}</Text>
          </View>
          {tipoFin !== "todos" && (
            <View style={{ textAlign: "center", width: "94%", fontSize: 9 }} fixed>
              <Text>
                Tipo de financiamiento: {TIPOS_FINANCIAMIENTO.find((t) => t.value === tipoFin)?.label}
              </Text>
            </View>
          )}

          {grupos.filter((g) => g.count > 0).map((g) => (
            <View key={g.key} wrap={false} style={{ marginLeft: 15, marginBottom: 8, width: "94%" }}>
              <View style={{ flexDirection: "row", alignItems: "center", backgroundColor: "#E6EAF1", padding: 4, marginBottom: 2 }}>
                <Text style={{ flex: 1, fontSize: 10, fontWeight: "bold" }}>{g.label}</Text>
                <Text style={{ width: 70, fontSize: 9, textAlign: "right" }}>Clientes: {g.count}</Text>
                <Text style={{ width: 130, fontSize: 9, textAlign: "right" }}>Vencido: {fmtMoney(g.totalAtraso)}</Text>
              </View>
              <View style={{ flexDirection: "row", height: 18, fontSize: 8, backgroundColor: "#F4F6FB", color: "black" }}>
                <Text style={{ width: 55, padding: 2 }}>Prest#</Text>
                <Text style={{ width: 80, padding: 2 }}>Cédula</Text>
                <Text style={{ width: 150, padding: 2 }}>Cliente</Text>
                <Text style={{ width: 80, padding: 2 }}>Estado</Text>
                <Text style={{ width: 90, padding: 2 }}>Chasis</Text>
                <Text style={{ width: 80, padding: 2, textAlign: "right" }}>Cap. venc.</Text>
                <Text style={{ width: 80, padding: 2, textAlign: "right" }}>Int. venc.</Text>
                <Text style={{ width: 85, padding: 2, textAlign: "right" }}>Total</Text>
                <Text style={{ width: 45, padding: 2, textAlign: "center" }}>Cuotas</Text>
                <Text style={{ width: 40, padding: 2, textAlign: "center" }}>Días</Text>
              </View>
              {g.items.map((r, i) => (
                <View key={i} style={{ flexDirection: "row", height: 13, fontSize: 8, color: "black" }}>
                  <Text style={{ width: 55, padding: 2 }}>{r.id}</Text>
                  <Text style={{ width: 80, padding: 2 }}>{r.cedula}</Text>
                  <Text style={{ width: 150, padding: 2 }}>{r.cliente}</Text>
                  <Text style={{ width: 80, padding: 2 }}>{r.estado_producto=="I"?"Incautado":"-"}</Text>
                  <Text style={{ width: 90, padding: 2 }}>{r.chasis}</Text>
                  <Text style={{ width: 80, padding: 2, textAlign: "right" }}>{fmtMoney(r.capital_overdue)}</Text>
                  <Text style={{ width: 80, padding: 2, textAlign: "right" }}>{fmtMoney(r.interest_overdue)}</Text>
                  <Text style={{ width: 85, padding: 2, textAlign: "right" }}>{fmtMoney(r.total_overdue)}</Text>
                  <Text style={{ width: 45, padding: 2, textAlign: "center" }}>{r.cuotasvencidas}</Text>
                  <Text style={{ width: 40, padding: 2, textAlign: "center" }}>{r.diasatraso}</Text>
                </View>
              ))}
            </View>
          ))}

          <View style={{ marginLeft: 15, marginTop: 8, width: "94%", flexDirection: "row", backgroundColor: "#E6EAF1", padding: 4 }}>
            <Text style={{ flex: 1, fontSize: 10, fontWeight: "bold" }}>TOTAL GENERAL</Text>
            <Text style={{ width: 80, fontSize: 9, textAlign: "right" }}>Clientes: {totalPrestamos}</Text>
            <Text style={{ width: 160, fontSize: 9, textAlign: "right" }}>Vencido: {fmtMoney(totalAtraso)}</Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos"
        subtitle="Antigüedad de Saldo"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={filteredRows.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: "#FFFFFF",
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
              "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: "#FAFBFE",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha de corte"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Cuotas vencidas mayor o igual a"
                  value={criterio1}
                  onChange={(event) => {
                    setCriterio1(event.target.value);
                    if (event.target.value) setCriterio2("");
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="number"
                  label="Cuotas vencidas igual a"
                  value={criterio2}
                  onChange={(event) => {
                    setCriterio2(event.target.value);
                    if (event.target.value) setCriterio1("");
                  }}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  disabled={!esadmin}
                  options={sucursal || []}
                  value={nosucursal}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return option?.label || "";
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const a = typeof option === "string" ? option : option?.label;
                    const b = typeof value === "string" ? value : value?.label;
                    return a === b;
                  }}
                  onChange={(_, value) => setNosucursal(value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Tipo de financiamiento"
                  value={tipoFin}
                  onChange={(event) => setTipoFin(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  {TIPOS_FINANCIAMIENTO.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  exclusive
                  value={prestamosPersonales ? "personal" : "general"}
                  onChange={(_, val) => {
                    if (val !== null) {
                      setPrestamosPersonales(val === "personal");
                      setDataObj({});
                    }
                  }}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 700,
                      border: `1px solid ${FP.color.border}`,
                      color: FP.color.textSecondary,
                      px: 2.4,
                      "&.Mui-selected": {
                        bgcolor: FP.color.navy,
                        color: "#FFFFFF",
                        "&:hover": { bgcolor: FP.color.navyDeep },
                      },
                    },
                  }}
                >
                  <ToggleButton value="general">General</ToggleButton>
                  <ToggleButton value="personal">Personal</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                disabled={cargando}
                startIcon={<SearchIcon />}
                onClick={procesarrequerimiento}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: "#FFFFFF",
                  borderRadius: 1.4,
                  textTransform: "none",
                  fontSize: "0.94rem",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
                }}
              >
                {cargando ? "Procesando..." : "Procesar"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <>
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard label="Préstamos vencidos" value={String(totalPrestamos)} icon={GroupsRoundedIcon} accent={FP.color.accentBlue} />
              <StatCard label="Total vencido" value={fmtMoney(totalAtraso)} icon={PaidRoundedIcon} accent={FP.color.danger} />
              <StatCard label="Capital vencido" value={fmtMoney(totalCapitalVenc)} icon={AccountBalanceRoundedIcon} accent={FP.color.warning} />
              <StatCard label="Cuotas prom. venc." value={`${promedioCuotas}`} icon={WarningAmberRoundedIcon} accent={FP.color.navy} />
            </Box>

            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 1.5,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              {grupos.map((g) => (
                <Box
                  key={g.key}
                  onClick={() => g.count > 0 && toggleBucket(g.key)}
                  role={g.count > 0 ? "button" : undefined}
                  tabIndex={g.count > 0 ? 0 : -1}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    p: 1.4,
                    bgcolor: "#FFFFFF",
                    position: "relative",
                    overflow: "hidden",
                    cursor: g.count > 0 ? "pointer" : "default",
                    userSelect: "none",
                    opacity: g.count === 0 ? 0.55 : 1,
                    transition: "background-color 0.18s ease",
                    "&:hover": g.count > 0 ? { bgcolor: "#F4F6FB" } : undefined,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      bgcolor: g.accent,
                    },
                  }}
                >
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                    {g.label}
                  </Typography>
                  <Typography sx={{ color: FP.color.textPrimary, fontSize: "1.1rem", fontWeight: 800, mt: 0.4, lineHeight: 1.1 }}>
                    {g.count} <Typography component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, color: FP.color.textSecondary }}>clientes</Typography>
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 700, mt: 0.4, fontVariantNumeric: "tabular-nums" }}>
                    {fmtMoney(g.totalAtraso)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, préstamo, cédula o chasis"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...fpInputSx, flex: 1, minWidth: { xs: "100%", sm: 280 } }}
              />
              <Box sx={{ display: "flex", gap: 1, ml: { sm: "auto" } }}>
                <Button
                  size="small"
                  onClick={expandAll}
                  sx={{
                    color: FP.color.textSecondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
                  }}
                >
                  Expandir todo
                </Button>
                <Button
                  size="small"
                  onClick={collapseAll}
                  sx={{
                    color: FP.color.textSecondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
                  }}
                >
                  Colapsar todo
                </Button>
              </Box>
            </Box>
          </>
        )}

        {filteredRows.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                  Seleccione la fecha de corte y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          grupos.filter((g) => g.count > 0).map((g) => {
            const isCollapsed = !!collapsed[g.key];
            return (
            <Card
              key={g.key}
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.cardBg,
                boxShadow: FP.shadow.soft,
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => toggleBucket(g.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleBucket(g.key);
                  }
                }}
                aria-expanded={!isCollapsed}
                sx={{
                  px: { xs: 2, md: 3 },
                  py: 1.4,
                  borderBottom: isCollapsed ? "none" : `1px solid ${FP.color.border}`,
                  bgcolor: "#FAFBFE",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  position: "relative",
                  cursor: "pointer",
                  userSelect: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: g.accent,
                  },
                  "&:hover": { bgcolor: "#F4F6FB" },
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: { xs: "0.95rem", sm: "1rem" } }}>
                    {g.label}
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.74rem", fontWeight: 600 }}>
                    {g.count} {g.count === 1 ? "cliente" : "clientes"} · Vencido {fmtMoney(g.totalAtraso)}
                  </Typography>
                </Box>
                <StatusPill label={`${g.count}`} variant={g.variant} />
                <IconButton
                  size="small"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleBucket(g.key);
                  }}
                  aria-label={isCollapsed ? "Expandir" : "Colapsar"}
                  sx={{
                    color: FP.color.textSecondary,
                    transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                >
                  <ExpandMoreRoundedIcon />
                </IconButton>
              </Box>
              {!isCollapsed && (
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0,1fr))" },
                    gap: 1.4,
                  }}
                >
                  {g.items.map((row, idx) => (
                    <Box
                      key={`${row.id}-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: "#FFFFFF",
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                            Préstamo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                            {row.cliente || "—"}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                            {row.cedula || "—"}{row.chasis ? ` · Chasis ${row.chasis}` : ""}
                          </Typography>
                          <Typography sx={{ color: "red", fontSize: "0.75rem", mt: 0.1 }}> 
                            {row.estado_producto=="I"?"Incautado":"-"}
                          </Typography>
                        </Box>
                        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.6 }}>
                          <StatusPill
                            label={`${row.cuotasvencidas} cuotas · ${row.diasatraso} días`}
                            variant={g.variant}
                          />
                          {buildWhatsappLink(row, nombreNegocio) && (
                            <Button
                              size="small"
                              component="a"
                              href={buildWhatsappLink(row, nombreNegocio)}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                bgcolor: "#25D366",
                                color: "#FFFFFF",
                                borderRadius: 1.2,
                                px: 1.2,
                                py: 0.3,
                                minWidth: 0,
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#1EBE57", boxShadow: "none" },
                              }}
                            >
                              WhatsApp
                            </Button>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 1 }}>
                        {[
                          ["Total vencido", fmtMoney(row.total_overdue)],
                          ["Cap. venc.", fmtMoney(row.capital_overdue)],
                          ["Int. venc.", fmtMoney(row.interest_overdue)],
                          ["Paga por", row.periodos || "—"],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, fontVariantNumeric: "tabular-nums", wordBreak: "break-word" }}>
                              {value ?? "—"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
              )}
            </Card>
            );
          })
        )}

        {filteredRows.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" }, mt: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={handlepdf}
              sx={{
                width: { xs: "100%", sm: "auto" },
                borderColor: FP.color.danger,
                color: FP.color.danger,
                borderRadius: 1.2,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
              }}
            >
              Generar PDF
            </Button>
          </Box>
        )}

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa de Antigüedad de Saldo
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}

export default ReporteAntiguedadSaldo;
